import {
  AspectRatio,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { ModalToSeeCovers } from "../../../components/ModalToSeeCovers";
import { CreateBookings } from "../Create";
import { DetailHeaderRentalArea } from "../Details";

/**
 * @callback childrenRender
 * @param {{disclosure: import("@chakra-ui/react").UseDisclosureReturn}} disclosure
 */
/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @property {childrenRender} children
 */
/**
 * @param {Props} props
 */
export function ViewBookings({ rentalArea, children }) {
  const translate = useTranslate();

  const disclosure = useDisclosure();

  const { isOpen, onClose } = disclosure;

  const [isOpenModal, setIsOpenModal] = useState(false);

  const format =
    useBreakpointValue({ base: "default", lg: "large" }) ?? "default";

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        scrollBehavior="inside"
        isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalCloseButton />

          <ModalHeader>
            <Text variant="h2" fontWeight={500}>
              <FormattedMessage defaultMessage="Détails de la salle" />
            </Text>
          </ModalHeader>

          <ModalBody pt="0">
            <DetailHeaderRentalArea rentalArea={rentalArea} />

            <VStack align="start" spacing="1rem">
              {rentalArea?.covers && (rentalArea.covers ?? []).length > 0 && (
                <VStack align="start">
                  <Text variant="h5" fontWeight={500}>
                    <FormattedMessage defaultMessage="Photos" />
                  </Text>

                  <HStack wrap="wrap" gap="1rem">
                    {rentalArea.covers.map((cover, index) => (
                      <ModalToSeeCovers
                        covers={rentalArea.covers ?? []}
                        isInModal={true}
                        currentCover={index}
                        key={cover.id}>
                        {function ({ disclosure }) {
                          return (
                            <AspectRatio
                              ratio={16 / 9}
                              w="170px"
                              h="95px"
                              onClick={disclosure.onOpen}>
                              <PampaImage
                                image={cover}
                                alt={translate(cover.caption) ?? ""}
                                format={format}
                                borderRadius="4px"
                                mode="crop"
                                placeholderSrc={generateFrontPath({
                                  id: "@front.internal-assets",
                                  parameters: { filePath: "placeholder.svg" },
                                  includeBasePath: true,
                                })}
                                _groupHover={{
                                  transform: "scale(1.1)",
                                }}
                                transition="all 0.25s ease"
                                bg="gray.100"
                              />
                            </AspectRatio>
                          );
                        }}
                      </ModalToSeeCovers>
                    ))}
                  </HStack>
                </VStack>
              )}

              {rentalArea.public_description && (
                <VStack align="start">
                  <Text variant="h5" fontWeight={500}>
                    <FormattedMessage defaultMessage="Description" />
                  </Text>

                  <Text color="gray.600" wordBreak="break-word">
                    {translate(rentalArea.public_description)}
                  </Text>
                </VStack>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            {rentalArea?.is_bookable_online && (
              <>
                <CreateBookings
                  rentalArea={rentalArea}
                  isInModal={true}
                  isOpen={isOpenModal}
                  onClose={() => setIsOpenModal(false)}
                />

                <Button
                  colorScheme="brandPrimary"
                  backgroundColor="brandPrimary.700"
                  onClick={() => {
                    setIsOpenModal(true);
                  }}>
                  <Text variant="button" color="inherit">
                    <FormattedMessage defaultMessage="Réserver en ligne" />
                  </Text>
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {children({ disclosure })}
    </>
  );
}
